import {
  type KeyboardEvent,
  type KeyboardEventHandler,
  type MouseEventHandler,
  type RefObject,
  useCallback,
} from 'react';

interface UseInteractiveElementKeyDownCallbackPropsInterface {
  ref: RefObject<HTMLDivElement>;
  onClick?: MouseEventHandler;
  onKeyDown?: KeyboardEventHandler;
  bubbleUp?: boolean;
}

/**
 * Hook that creates an accessible keyboard event handler for interactive elements.
 * Makes non-interactive elements (like divs) behave like buttons by triggering clicks
 * when Enter or Space keys are pressed. Enhances accessibility compliance.
 *
 * @param {object} props - Configuration object for the keyboard handler.
 * @param {RefObject<HTMLDivElement>} props.ref - Ref to the target element.
 * @param {MouseEventHandler} [props.onClick] - Click handler to trigger on Enter/Space.
 * @param {KeyboardEventHandler} [props.onKeyDown] - Additional keyboard event handler.
 * @param {boolean} [props.bubbleUp=false] - Whether to handle events from child elements.
 * @returns {KeyboardEventHandler<HTMLDivElement>} Memoized keyboard event handler function.
 */
export const useInteractiveElementKeyDownCallback = ({
  ref,
  onClick,
  onKeyDown,
  bubbleUp = false,
}: UseInteractiveElementKeyDownCallbackPropsInterface) => {
  return useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.target !== ref.current && !bubbleUp) {
        return;
      }

      if (typeof onKeyDown === 'function') {
        onKeyDown(event);
      }

      if (['Enter', 'Space'].includes(event.code)) {
        if (typeof onClick === 'function') {
          ref.current?.click();
          event.preventDefault();
        }
      }
    },
    [ref, onClick, onKeyDown, bubbleUp],
  );
};
